<template>
    <div class="auth-container">
      <div class="auth-card">
        <!-- Logo/Title -->
        <div class="auth-header">
          <h2>CVteque</h2>
          <p>Resume Search Platform</p>
        </div>
  
        <!-- Sign In Form -->
        <div v-if="authMode === 'signin'" class="auth-form">
          <h3>Sign In</h3>
          
          <form @submit.prevent="handleSignIn">
            <div class="form-group">
              <label>Email</label>
              <input 
                type="email" 
                v-model="signInForm.email" 
                required 
                placeholder="Enter your email"
              />
            </div>
            
            <div class="form-group">
              <label>Password</label>
              <input 
                type="password" 
                v-model="signInForm.password" 
                required 
                placeholder="Enter your password"
              />
            </div>
            
            <button type="submit" :disabled="isLoading" class="auth-button primary">
              <span v-if="isLoading">Signing in...</span>
              <span v-else>Sign In</span>
            </button>
          </form>
          
          <div class="auth-links">
            <a href="#" @click="authMode = 'signup'">Don't have an account? Sign up</a>
            <a href="#" @click="authMode = 'forgot'">Forgot password?</a>
          </div>
        </div>
  
        <!-- Sign Up Form -->
        <div v-if="authMode === 'signup'" class="auth-form">
          <h3>Create Account</h3>
          
          <form @submit.prevent="handleSignUp">
            <div class="form-row">
              <div class="form-group">
                <label>First Name</label>
                <input 
                  type="text" 
                  v-model="signUpForm.firstName" 
                  required 
                  placeholder="First name"
                />
              </div>
              <div class="form-group">
                <label>Last Name</label>
                <input 
                  type="text" 
                  v-model="signUpForm.lastName" 
                  required 
                  placeholder="Last name"
                />
              </div>
            </div>
            
            <div class="form-group">
              <label>Email</label>
              <input 
                type="email" 
                v-model="signUpForm.email" 
                required 
                placeholder="Enter your email"
              />
            </div>
            
            <div class="form-group">
              <label>Password</label>
              <input 
                type="password" 
                v-model="signUpForm.password" 
                required 
                placeholder="Create a password"
              />
            </div>
            
            <button type="submit" :disabled="isLoading" class="auth-button primary">
              <span v-if="isLoading">Creating account...</span>
              <span v-else>Create Account</span>
            </button>
          </form>
          
          <div class="auth-links">
            <a href="#" @click="authMode = 'signin'">Already have an account? Sign in</a>
          </div>
        </div>
  
        <!-- Email Verification Form -->
        <div v-if="authMode === 'verify'" class="auth-form">
          <h3>Verify Email</h3>
          <p>We sent a verification code to {{ signUpForm.email }}</p>
          
          <form @submit.prevent="handleVerification">
            <div class="form-group">
              <label>Verification Code</label>
              <input 
                type="text" 
                v-model="verificationCode" 
                required 
                placeholder="Enter verification code"
              />
            </div>
            
            <button type="submit" :disabled="isLoading" class="auth-button primary">
              <span v-if="isLoading">Verifying...</span>
              <span v-else>Verify Email</span>
            </button>
          </form>
          
          <div class="auth-links">
            <a href="#" @click="resendCode">Resend code</a>
            <a href="#" @click="authMode = 'signin'">Back to sign in</a>
          </div>
        </div>
  
        <!-- Forgot Password Form -->
        <div v-if="authMode === 'forgot'" class="auth-form">
          <h3>Reset Password</h3>
          
          <form @submit.prevent="handleForgotPassword">
            <div class="form-group">
              <label>Email</label>
              <input 
                type="email" 
                v-model="forgotForm.email" 
                required 
                placeholder="Enter your email"
              />
            </div>
            
            <button type="submit" :disabled="isLoading" class="auth-button primary">
              <span v-if="isLoading">Sending...</span>
              <span v-else>Send Reset Code</span>
            </button>
          </form>
          
          <div class="auth-links">
            <a href="#" @click="authMode = 'signin'">Back to sign in</a>
          </div>
        </div>
  
        <!-- Error Messages -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
  
        <!-- Success Messages -->
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import authService from '../services/auth.js';
  
  export default {
    name: 'AuthForm',
    data() {
      return {
        authMode: 'signin', // signin, signup, verify, forgot
        isLoading: false,
        errorMessage: '',
        successMessage: '',
        verificationCode: '',
        
        signInForm: {
          email: '',
          password: ''
        },
        
        signUpForm: {
          email: '',
          password: '',
          firstName: '',
          lastName: ''
        },
        
        forgotForm: {
          email: ''
        }
      };
    },
    
    methods: {
      async handleSignIn() {
        this.isLoading = true;
        this.errorMessage = '';
        
        try {
          const result = await authService.signIn(
            this.signInForm.email, 
            this.signInForm.password
          );
          console.log('🔍 AuthForm: signIn result:', result);
          
          if (result.success) {
            console.log('✅ Normal login success');
            this.$emit('authenticated');
          }
          else if (result.requiresNewPassword) {
            // NEW_PASSWORD_REQUIRED challenge detected
            console.log('🔄 Redirecting to password change page');
           try {
            // Use $router directly - this should work
            await this.$router.push('/change-password');
            console.log('✅ Navigation to /change-password successful');
          } catch (navError) {
            console.error('❌ Navigation error:', navError);
            this.errorMessage = 'Navigation failed. Please try refreshing the page.';
          }
        }  else {
            console.log('❌ Login failed:', result.error);
            this.errorMessage = result.error;
          }
        } catch (error) {
          console.error('❌ HandleSignIn error:', error);
          this.errorMessage = 'Sign in failed. Please try again.';
        }
        
        this.isLoading = false;
      },
      
      async handleSignUp() {
        this.isLoading = true;
        this.errorMessage = '';
        
        try {
          const result = await authService.signUp(
            this.signUpForm.email,
            this.signUpForm.password,
            this.signUpForm.firstName,
            this.signUpForm.lastName
          );
          
          if (result.success) {
            this.authMode = 'verify';
            this.successMessage = 'Account created! Please check your email for verification code.';
          } else {
            this.errorMessage = result.error;
          }
        } catch (error) {
          this.errorMessage = 'Sign up failed. Please try again.';
        }
        
        this.isLoading = false;
      },
      
      async handleVerification() {
        this.isLoading = true;
        this.errorMessage = '';
        
        try {
          const result = await authService.confirmSignUp(
            this.signUpForm.email,
            this.verificationCode
          );
          
          if (result.success) {
            this.successMessage = 'Email verified! You can now sign in.';
            this.authMode = 'signin';
            this.verificationCode = '';
          } else {
            this.errorMessage = result.error;
          }
        } catch (error) {
          this.errorMessage = 'Verification failed. Please try again.';
        }
        
        this.isLoading = false;
      },
      
      async handleForgotPassword() {
        this.isLoading = true;
        this.errorMessage = '';
        
        try {
          const result = await authService.forgotPassword(this.forgotForm.email);
          
          if (result.success) {
            this.successMessage = 'Reset code sent to your email!';
          } else {
            this.errorMessage = result.error;
          }
        } catch (error) {
          this.errorMessage = 'Failed to send reset code. Please try again.';
        }
        
        this.isLoading = false;
      },
      
      async resendCode() {
        this.isLoading = true;
        this.errorMessage = '';
        
        try {
          const result = await authService.resendConfirmationCode(this.signUpForm.email);
          
          if (result.success) {
            this.successMessage = 'Verification code resent!';
          } else {
            this.errorMessage = result.error;
          }
        } catch (error) {
          this.errorMessage = 'Failed to resend code. Please try again.';
        }
        
        this.isLoading = false;
      }
    },
    
    watch: {
      authMode() {
        // Clear messages when switching modes
        this.errorMessage = '';
        this.successMessage = '';
      }
    }
  };
  </script>
  
  <style scoped>
  .auth-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--background-color);
    padding: 2rem;
  }
  
  .auth-card {
    background: var(--surface-color);
    border: 1px solid var(--border-color);
    border-radius: 1rem;
    padding: 2.5rem;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
  
  .auth-header {
    text-align: center;
    margin-bottom: 2rem;
  }
  
  .auth-header h2 {
    margin: 0 0 0.5rem 0;
    color: var(--primary-color);
    font-size: 2rem;
    font-weight: 600;
  }
  
  .auth-header p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
  }
  
  .auth-form h3 {
    margin: 0 0 1.5rem 0;
    color: var(--text-color);
    font-size: 1.5rem;
    font-weight: 500;
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-color);
    font-weight: 500;
    font-size: 0.9rem;
  }
  
  .form-group input {
    width: 100%;
    padding: 0.75rem 1rem;
    background: var(--background-color);
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    color: var(--text-color);
    font-size: 0.95rem;
    transition: all 0.2s ease;
  }
  
  .form-group input:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(212, 47, 61, 0.1);
  }
  
  .auth-button {
    width: 100%;
    padding: 0.875rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 1.5rem;
  }
  
  .auth-button.primary {
    background: var(--primary-color);
    color: white;
  }
  
  .auth-button.primary:hover:not(:disabled) {
    background: #b52835;
    transform: translateY(-1px);
  }
  
  .auth-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  .auth-links {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }
  
  .auth-links a {
    color: var(--primary-color);
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;
  }
  
  .auth-links a:hover {
    color: #b52835;
    text-decoration: underline;
  }
  
  .error-message {
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #f44336;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
    font-size: 0.9rem;
  }
  
  .success-message {
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.3);
    color: #4CAF50;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    .auth-container {
      padding: 1rem;
    }
    
    .auth-card {
      padding: 2rem;
    }
    
    .form-row {
      grid-template-columns: 1fr;
    }
  }
  </style>